/**
 * HVAC Discipline Configuration
 * 
 * This configuration defines all aspects of the HVAC matrix:
 * - Column definitions
 * - Field mappings (frontend <-> backend)
 * - Calculations
 * - API endpoints
 * - Permissions
 * - Available features
 */

/**
 * Field mapping from frontend camelCase to backend snake_case
 */
export const FIELD_MAP = {
  buildingNumber: 'building_number',
  area: 'area',
  subArea: 'sub_area',
  elevation: 'elevation',
  floor: 'floor',
  equipmentTagOrCode: 'equipment_tag_or_code',
  description: 'description',
  budgetedHr: 'budgeted_hr',
  credit: 'credit',
  costCode: 'cost_code',
  sovCode: 'sov_code',
  sovDescription: 'sov_description',
  deliveryToSite: 'delivery_to_site',
  roughInstall: 'rough_install',
  supports: 'supports',
  accessories: 'accessories',
  airBalancing: 'air_balancing',
  drawingNumber: 'drawing_number',
  pAndId: 'p_and_id',
  divisionArea: 'division_area',
  subDivisionArea: 'sub_division_area',
  activityId: 'activity_id',
  activityName: 'activity_name',
  comments: 'comments',
  udf01: 'udf01',
  udf02: 'udf02',
  udf03: 'udf03',
  udf04: 'udf04',
  udf05: 'udf05',
  udf06: 'udf06',
  udf07: 'udf07',
  udf08: 'udf08',
  udf09: 'udf09',
  udf10: 'udf10',
};

/**
 * Columns that affect the total_field_completion calculation
 */
export const FIELD_COMPLETION_COLUMNS = [
  'deliveryToSite',
  'roughInstall',
  'supports',
  'accessories',
  'airBalancing',
];

/**
 * Editable columns for progress tracking
 */
export const EDITABLE_COLUMNS = [
  'activityId', 'activityName',
  'buildingNumber',
  'area',
  'subArea',
  'elevation',
  'floor',
  'equipmentTagOrCode',
  'description',
  'budgetedHr',
  'credit',
  'costCode',
  'sovCode',
  'deliveryToSite',
  'roughInstall',
  'supports',
  'accessories',
  'airBalancing',
  'drawingNumber',
  'pAndId',
  'divisionArea',
  'subDivisionArea',
  'comments',
  'udf01', 'udf02', 'udf03', 'udf04', 'udf05',
  'udf06', 'udf07', 'udf08', 'udf09', 'udf10',
];

/**
 * Transform raw API data to frontend format
 * Order matches dashboard column sequence
 */
export const transformMatrixData = (matrixData) => {
  return matrixData.map((row, index) => ({
    id: row.id || index,
    area: row.area || '',
    subArea: row.sub_area || '',
    buildingNumber: row.building_number || '',
    floor: row.floor || '',
    divisionArea: row.division_area || '',
    subDivisionArea: row.sub_division_area || '',
    jobNumber: row.job_number || '',
    activityId: row.activity_id || '',
    activityName: row.activity_name || '',
    costCode: row.cost_code || '',
    sovCode: row.sov_code || '',
    sovDescription: row.sov_description || '',
    elevation: row.elevation || '',
    pAndId: row.p_and_id || '',
    drawingNumber: row.drawing_number || '',
    equipmentTagOrCode: row.equipment_tag_or_code || '',
    description: row.description || '',
    deliveryToSite: row.delivery_to_site ? Math.round(row.delivery_to_site) : 0,
    roughInstall: row.rough_install ? Math.round(row.rough_install) : 0,
    supports: row.supports ? Math.round(row.supports) : 0,
    accessories: row.accessories ? Math.round(row.accessories) : 0,
    airBalancing: row.air_balancing ? Math.round(row.air_balancing) : 0,
    totalFieldCompletion: row.total_field_completion ? Math.round(row.total_field_completion) : 0,
    budgetedHr: row.budgeted_hr ? Number(row.budgeted_hr) : 0,
    credit: row.credit ? Number(row.credit) : 0,
    earnedHr: row.earned_hr ? Number(row.earned_hr) : 0,
    earnedCredit: row.earned_credit ? Number(row.earned_credit) : 0,
    createdAt: row.created_at || '',
    updatedAt: row.updated_at || '',
    comments: row.comments || '',
    udf01: row.udf01 || '',
    udf02: row.udf02 || '',
    udf03: row.udf03 || '',
    udf04: row.udf04 || '',
    udf05: row.udf05 || '',
    udf06: row.udf06 || '',
    udf07: row.udf07 || '',
    udf08: row.udf08 || '',
    udf09: row.udf09 || '',
    udf10: row.udf10 || '',
  }));
};

/**
 * Build change payload for saving a cell change
 */
export const buildChangePayload = (rowId, columnId, oldValue, newValue) => {
  const backendField = FIELD_MAP[columnId];
  if (!backendField) return null;

  return {
    id: rowId,
    changes: {
      [backendField]: newValue,
    },
  };
};

/**
 * HVAC discipline configuration
 */
export const hvacConfig = {
  id: 'hvac',
  name: 'HVAC',
  menuId: 'hvac',
  tableName: 'hvac_tracking',
  historyTableName: 'hvac_tracking_history',
  
  // API endpoints
  api: {
    getData: '/discipline-matrix/hvac/project/{projectId}',
    saveChanges: '/discipline-matrix/hvac/save-changes',
    export: '/discipline-matrix/hvac/export/{projectId}',
    upload: '/discipline-matrix/hvac/upload',
    priorities: '/priorities',
    columnMappings: '/discipline-matrix/hvac/project/{projectId}/column-mappings',
  },
  
  // Permission requirements (pt_* = Progress Tracking module)
  permissions: {
    edit: 'pt_edit',
    analytics: 'pt_analytics',
    import: 'pt_import',
    export: 'pt_export',
    template: 'pt_template',
    columnConfig: 'pt_columnconfig',
    roc: 'pt_roc',
    sfRatio: 'pt_sfratio',
    matBore: 'pt_matbore',
    delete: 'pt_delete',
  },
  
  // Column definitions - full names with text wrapping
  columns: [
    // Dashboard columns - full names
    { id: 'id', header: 'ID', accessor: 'id', dbField: 'id', width: 50, editable: false, filterable: true, type: 'number', showInDashboard: true },
    { id: 'area', header: 'Area', accessor: 'area', dbField: 'area', width: 85, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'subArea', header: 'Sub Area', accessor: 'subArea', dbField: 'sub_area', width: 85, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'buildingNumber', header: 'Building Number', accessor: 'buildingNumber', dbField: 'building_number', width: 85, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'floor', header: 'Floor', accessor: 'floor', dbField: 'floor', width: 65, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'divisionArea', header: 'Division Area', accessor: 'divisionArea', dbField: 'division_area', width: 85, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'subDivisionArea', header: 'Sub Division Area', accessor: 'subDivisionArea', dbField: 'sub_division_area', width: 85, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'jobNumber', header: 'Job Number', accessor: 'jobNumber', dbField: 'job_number', width: 85, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'activityId', header: 'Activity ID', accessor: 'activityId', dbField: 'activity_id', width: 130, editable: true, filterable: true, type: 'text', showInDashboard: true, headerGroup: 'Schedule' },
    { id: 'activityName', header: 'Activity Name', accessor: 'activityName', dbField: 'activity_name', width: 160, editable: true, filterable: true, type: 'text', showInDashboard: false, headerGroup: 'Schedule' },
    { id: 'costCode', header: 'Cost Code', accessor: 'costCode', dbField: 'cost_code', width: 85, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'sovCode', header: 'SOV Code', accessor: 'sovCode', dbField: 'sov_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'elevation', header: 'Elevation', accessor: 'elevation', dbField: 'elevation', width: 75, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'pAndId', header: 'P&ID', accessor: 'pAndId', dbField: 'p_and_id', width: 75, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'drawingNumber', header: 'Drawing Number', accessor: 'drawingNumber', dbField: 'drawing_number', width: 85, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'equipmentTagOrCode', header: 'Equipment Tag/Code', accessor: 'equipmentTagOrCode', dbField: 'equipment_tag_or_code', width: 95, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'description', header: 'Description', accessor: 'description', dbField: 'description', width: 130, editable: true, filterable: true, type: 'text', showInDashboard: true },
    // Progress columns
    { id: 'deliveryToSite', header: 'Delivery to Site %', accessor: 'deliveryToSite', dbField: 'delivery_to_site', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E3F2FD', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'roughInstall', header: 'Rough Install %', accessor: 'roughInstall', dbField: 'rough_install', width: 75, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E3F2FD', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'supports', header: 'Supports %', accessor: 'supports', dbField: 'supports', width: 75, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E3F2FD', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'accessories', header: 'Accessories %', accessor: 'accessories', dbField: 'accessories', width: 75, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E3F2FD', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'airBalancing', header: 'Air Balancing %', accessor: 'airBalancing', dbField: 'air_balancing', width: 80, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#E3F2FD', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'totalFieldCompletion', header: 'Total Field Completion %', accessor: 'totalFieldCompletion', dbField: 'total_field_completion', width: 80, editable: false, filterable: true, type: 'progress-readonly', computed: true, headerColor: '#E3F2FD', headerGroup: 'Field Progress', showInDashboard: true },
    // Hidden from dashboard
    { id: 'budgetedHr', header: 'Budgeted Hr', accessor: 'budgetedHr', dbField: 'budgeted_hr', width: 90, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'credit', header: 'Credit', accessor: 'credit', dbField: 'credit', width: 70, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'earnedHr', header: 'Earned Hr', accessor: 'earnedHr', dbField: 'earned_hr', width: 90, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'earnedCredit', header: 'Earned Credit', accessor: 'earnedCredit', dbField: 'earned_credit', width: 100, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'createdAt', header: 'Created At', accessor: 'createdAt', dbField: 'created_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
    { id: 'updatedAt', header: 'Updated At', accessor: 'updatedAt', dbField: 'updated_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
    { id: 'comments', header: 'Comments', accessor: 'comments', dbField: 'comments', width: 120, editable: true, filterable: true, type: 'text', showInDashboard: false },
  ],
  
  // UDF columns
  udfColumns: Array.from({ length: 10 }, (_, i) => ({
    id: `udf${String(i + 1).padStart(2, '0')}`,
    header: `UDF ${String(i + 1).padStart(2, '0')}`,
    accessor: `udf${String(i + 1).padStart(2, '0')}`,
    dbField: `udf${String(i + 1).padStart(2, '0')}`,
    width: 100,
    editable: true,
    filterable: true,
    type: 'text',
    isUdf: true,
  })),
  
  // Field mappings
  fieldMap: FIELD_MAP,
  editableColumns: EDITABLE_COLUMNS,
  
  // Data transformation
  transformData: transformMatrixData,
  buildChangePayload: buildChangePayload,
  
  // Available features
  features: {
    dragFill: true,
    progressTrends: true,
    matBore: false,
    rulesOfCredit: true,
    columnConfig: true,
    actualSpentUpload: false,
    export: true,
    upload: true,
    presence: true,
  },
};

export default hvacConfig;

